/** Windowed page numbers for host listings pager (e.g. 1 … 4 5 6 … 12). */
export function buildPageWindow(
  currentPage: number,
  totalPages: number,
  siblingCount = 1,
): Array<number | 'ellipsis'> {
  if (totalPages <= 0) return [];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>();
  pages.add(1);
  pages.add(totalPages);
  for (let p = currentPage - siblingCount; p <= currentPage + siblingCount; p += 1) {
    if (p >= 1 && p <= totalPages) pages.add(p);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out: Array<number | 'ellipsis'> = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push('ellipsis');
    out.push(p);
    prev = p;
  }
  return out;
}

export function showingRange(
  page: number,
  limit: number,
  totalCount: number,
): { from: number; to: number } {
  if (totalCount <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalCount);
  return { from, to };
}

/** Step back one page when the current page returned zero items. */
export function clampPageAfterEmpty(page: number, itemCount: number): number {
  if (itemCount > 0) return page;
  return Math.max(1, page - 1);
}

/**
 * Resolve a valid URL page from a response that matches the requested page.
 * Prefer jumping to `totalPages` over stepping back one-by-one.
 */
export function resolveValidPage(
  page: number,
  totalPages: number,
  itemCount: number,
  totalCount: number,
): number {
  if (!Number.isFinite(page) || page < 1) return 1;
  if (totalCount <= 0) return 1;
  if (totalPages > 0 && page > totalPages) return totalPages;
  if (itemCount === 0 && page > 1) {
    return Math.max(1, totalPages > 0 ? Math.min(page - 1, totalPages) : page - 1);
  }
  return page;
}

/** Prefer a page payload that matches the requested page; avoid flashing the wrong page. */
export function pickMatchingPageData<T extends { page: number }>(
  requestedPage: number,
  current?: T | null,
  previous?: T | null,
): T | undefined {
  if (current?.page === requestedPage) return current;
  if (previous?.page === requestedPage) return previous;
  return undefined;
}

/**
 * Merge paginated list pages using the response page (not local request state),
 * so stale Apollo payloads cannot replace/append the wrong slice.
 */
export function mergePagedItems<T extends { id: string | number }>(
  previous: T[],
  responsePage: number,
  items: T[],
): T[] {
  if (responsePage <= 1) return items;
  const seen = new Set(previous.map(c => c.id));
  return [...previous, ...items.filter(c => !seen.has(c.id))];
}
